import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { doc, getDoc, addDoc, collection } from "firebase/firestore";
import { db } from "../firebase";
import ReactMarkdown from "react-markdown";
import { getMarkdownComponents } from "../utils/markdown";
import grayBackground from "../images/gray-background.png";
import { auth } from "../firebase";
import BackButton from "../components/BackButton";

function LessonOverview({ userType = "user" }) {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Track lesson access
  const trackLessonAccess = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error("No authenticated user found");
        return;
      }

      // Get user data to find organization_id
      const userDoc = await getDoc(doc(db, "users", currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();

        // Create lesson access activity record
        const activityData = {
          created_date: new Date(),
          user_id: doc(db, "users", currentUser.uid),
          organization_id: userData.organization_id,
          type: "accessed_lesson",
          lesson_id: lessonId,
        };

        // Add to user_activity collection
        const activityRef = collection(db, "user_activity");
        await addDoc(activityRef, activityData);
      }
    } catch (error) {
      console.error("Error tracking lesson access:", error);
      // Don't throw error - lesson tracking failure shouldn't prevent lesson access
    }
  };

  // Fetch lesson from Firestore
  useEffect(() => {
    const fetchLesson = async () => {
      try {
        const lessonRef = doc(db, "node_lessons", lessonId);
        const lessonSnap = await getDoc(lessonRef);

        if (lessonSnap.exists()) {
          const lessonData = {
            id: lessonSnap.id,
            ...lessonSnap.data(),
          };

          setLesson(lessonData);
        } else {
          setError("Lesson not found");
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching lesson:", error);
        setError("Failed to load lesson");
        setLoading(false);
      }
    };

    if (lessonId) {
      fetchLesson();
    }
  }, [lessonId, userType]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading lesson...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-8">
              <p className="text-gray-600">Lesson not found.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="py-8 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Top Section - Lesson Overview */}
          <div className="bg-white rounded-lg mb-8">
            {/* Navigation */}
            <BackButton to={location.state?.returnPath || "/lessons"} />

            {/* Age Range and Units */}
            {/*<div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600 font-light">
                {formatAgeGroups(lesson.age_groups)}
              </p>
              <p className="text-sm text-gray-600 font-light">
                {formatUnits(lesson.units)}
              </p>
            </div>*/}

            {/* Main Content */}
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left Side - Text Content */}
              <div className="flex-1">
                {/* Title */}
                <h1 className="text-4xl font-bold text-gray-900 mb-4">
                  {lesson.title || "No title"}
                </h1>

                {/* Description */}
                <p className="text-gray-700 text-md leading-relaxed mb-5">
                  {lesson.description || "No description available"}
                </p>

                {/* Ages */}
                {lesson.age_groups &&
                  Array.isArray(lesson.age_groups) &&
                  lesson.age_groups.length > 0 && (
                    <p className="text-sm text-gray-600 font-light mb-5 uppercase">
                      Ages{" "}
                      {lesson.age_groups
                        .map((ag) =>
                          typeof ag === "string" ? ag : ag?.description || "",
                        )
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  )}

                {/* Start Lesson Button */}
                <button
                  onClick={async () => {
                    await trackLessonAccess();
                    navigate(`/lesson-steps/${lessonId}`);
                  }}
                  className="bg-primary-500 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition-colors font-semibold"
                >
                  Start Lesson
                </button>
              </div>

              {/* Right Side - Image */}
              <div className="lg:w-2/5">
                {lesson.cover_image ? (
                  <img
                    src={lesson.cover_image}
                    alt={lesson.title || "Lesson cover"}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center">
                    <p className="text-gray-500">No image available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Section - Activity Prep */}
          <div className="lg">
            <div
              className="rounded-lg p-8"
              style={{
                backgroundImage: `url(${grayBackground})`,
                backgroundRepeat: "repeat",
                backgroundSize: "auto",
              }}
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Activity Prep
              </h2>

              {lesson.activity_instructions ? (
                <div className="text-gray-700 leading-relaxed prose prose-gray max-w-none">
                  <ReactMarkdown components={getMarkdownComponents()}>
                    {lesson.activity_instructions}
                  </ReactMarkdown>
                </div>
              ) : (
                <p className="text-gray-600">
                  No activity instructions available.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LessonOverview;
